import GoogleMaps from '../functionality-classes/google-maps';
import MapLocation from '../models/map-location-types/map-location';

/*
    The Map class in an abstraction layer on top of GoogleMaps class, and is used to instaniate the GoogleMaps class in a safe manner.
    It can also be used as an refactoring entrypoint to abstract the GoogleMaps class away and use another map provider.
    The Map class takes a list of locations, that can be either "typed-locations" or "untyped-locations".
    Untyped locations are regular data objects, where typed locations have been instantiated to a class that inherits from MapLocation.
    This class ensures that all locations are typed, by instantiating them if needed using a component repository.

    ARGS:
    -googleMaps: object that contains args for GoogleMaps component (locations can be excluded since they are overridden by the location or typedLocation property below)
    -locations : array of objects that the Map will try to instantiate to MapLocation instances for GoogleMaps
    -typedLocations (optional): array of instances of MapLocation or subclasses thereof. If this is passed, it will be used instead of "locations"
    -fitBoundsOnLocationSet (optional): boolean that indicates if the map view should be changed to fit bounds of locations when locations are set. Defaults to true.
    -mapClassRepository (optional): instance of ComponentRepository
*/
export default class Map {
    constructor(elm, args) {
        const self = this;
        this.fitBoundsOnLocationSet = typeof args.fitBoundsOnLocationSet === 'boolean' ? args.fitBoundsOnLocationSet : true;
        this.mapClassRepository = args.mapClassRepository || window.cr;

        args.clickEvents.forEach(x => document.querySelector(`input[id="${x.Id}"]`).addEventListener('input', y => {
            if (y.target.checked) {
                //Delay pan to first filter out the results before panning (which also updates the inbound results). To avoid async issues there (not the prettiest solution, but it works).
                setTimeout(() => {
                    self.gMaps.panTo({ zoom: x.Zoom, lat: x.Latitude, lng: x.Longitude })
                }, 10)
            }
        }))

        const instantiateMap = function (mapLocationsArray) {
            self.gMaps = new GoogleMaps(elm.querySelector('[data-map-google-maps'), {
                googleMapsAPIKey: args.googleMaps.googleMapsAPIKey,
                locations: mapLocationsArray,
                clusterIcon: args.googleMaps.clusterIcon,
                defaultMarkerImage: args.googleMaps.defaultMarkerImage,
                updateListView: args.updateListView,
                gMapsOptions: {
                    center: args.googleMaps.googleMapsOptions.center,
                    zoom: args.googleMaps.googleMapsOptions.zoom,
                    mapTypeControl: false,
                    streetViewControl: false,
                    styles: null,
                },
            });

            //if (!args.googleMaps.googleMapsOptions.center && (mapLocationsArray && mapLocationsArray.length > 0)) {
            //    self.gMaps.fitBounds();
            //}
        }

        // If the map has been instantiated with locations that have been pre-instantiated to valid MapLocation types
        // Instaniate the map
        if (args.typedLocations) {
            instantiateMap(args.typedLocations);
        } else { // Else instantiate all maps with a type and then instantiate the map
            this.instantiateAllMapLocations(args.locations)
                .then(instantiateMap);
        }
    }

    /*
    Loops through all map locations and instantiates by the locations component type
    */
    instantiateAllMapLocations(locationDataArray) {
        const instantiateMapLocationPromiseArray = [];

        // If no locations, return resolved promise
        if (!locationDataArray) {
            return new Promise((resolve) => {
                resolve();
            });
        }
        //console.log(locationDataArray)
        for (let i = 0; i < locationDataArray.length; i++) {
            instantiateMapLocationPromiseArray.push(this.instantiateMapLocation(locationDataArray[i]));
        }

        return Promise.all(instantiateMapLocationPromiseArray);
    }

    /*
    Gets the Class for a specific location based on its "component" property. If unset it will default to MapLocation class.
    This function loads the classes from the ComponentRepository, but it could in theory load them in a different way.
    */
    instantiateMapLocation(locationData) {
        return this.mapClassRepository.loadComponentClass(locationData.component)
            .then(MapTypeClass => {
                return new MapTypeClass(locationData);
            })
            .catch(() => {
                return new MapLocation(locationData);
            });
    }

    setLocations(locations, args, fitBounds) {
        return this.instantiateAllMapLocations(locations)
            .then(instantiatedLocations => {
                if (this.gMaps) {
                    this.gMaps.setLocations(instantiatedLocations, args);
                    //if (fitbounds !== undefined && fitbounds) {
                    //    this.gmaps.fitbounds();
                    //} else if (fitbounds === undefined && this.fitboundsonlocationset) {
                    //    this.gmaps.fitbounds();
                    //}
                }
            });
    }

    /*
        Same as setLocations, but this one requires that all locations have been instantiated to MapLocation or subclass thereof
    */
    setTypedLocations(locations, args, fitBounds) {
        this.gMaps.setLocations(locations, args);
        //if (fitBounds !== undefined && fitBounds) {
        //    this.gMaps.fitBounds();
        //} else if (fitBounds === undefined && this.fitBoundsOnLocationSet) {
        //    this.gMaps.fitBounds();
        //}
    }
}
